import { css } from "@emotion/react";
import { Button, Theme, useTheme } from "@mui/material";
import { ClientError } from "graphql-request";
import { GetStaticPropsContext, NextPage } from "next";
import Link from "next/link";
import { ChangeEvent, useCallback, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { UseFormSetValue } from "react-hook-form/dist/types/form";
import { match } from "ts-pattern";

import CreateAccountLead from "~/components/common/CreateAccountLead";
import PageHead from "~/components/common/PageHead";
import {
  AddRadioResponseInput,
  EMPTY_OPTION_VALUE,
  addRadioResponseInputSchema,
  options,
  useAddRadioResponse,
} from "~/extensions/radio-form/lib";
import { mediaQueryIsDesktop, mediaQueryIsMobile } from "~/lib/client/design";
import { useGraphqlClientWithAuth } from "~/lib/client/hooks/useGraphqlClient";
import useIsSpMode from "~/lib/client/hooks/useIsSpMode";
import { readBlobAsDataUrl } from "~/lib/client/images/useDataFromBlob";
import { Layout } from "~/lib/client/layout";
import {
  MediumType,
  RadioFormContentQuery,
  UploadFileForRadioResponseMutation,
} from "~/lib/graphql/client";
import { errorExtensionsSchema } from "~/lib/graphql/error-extensions";
import { createGraphqlClient } from "~/lib/graphql/graphqlClient";
import { useAppContext } from "~/pages/_app";

export async function getStaticPaths() {
  const result = await createGraphqlClient()
    .radioChannelsForSsg()
    .then((result) => {
      return result.radioChannels;
    })
    .catch((error) => {
      if (error instanceof ClientError) {
        const gqlErrors = error.response.errors;
        if (!gqlErrors) {
          throw error;
        }
        gqlErrors.forEach((gqlError) => {
          console.error(
            `[GraphQL error]: Message: ${gqlError.message}, Code: ${gqlError.extensions?.code}`
          );
        });

        return [];
      }
    });

  return {
    paths: result?.map((channel) => ({
      params: { id: channel.id.toString() },
    })),
    fallback: "blocking",
  };
}

export async function getStaticProps(context: GetStaticPropsContext) {
  const id = context.params?.id;
  if (typeof id !== "string") {
    return {
      notFound: true,
    };
  }

  if (Number(id) == 0) {
    return {
      notFound: true,
    };
  }

  const graphqlClient = createGraphqlClient();
  const { radioChannel } = await graphqlClient.radioFormContent({
    id: Number(id),
  });

  return {
    props: {
      radioChannel,
    },
    revalidate: 60 * 10, // 10 min
  };
}

type Props = RadioFormContentQuery;

const textAreaPlaceholderText = `お便りの入力はこちらへ！
ラジオネームもお忘れなく！
`;

const RadioChannelDetailPage: NextPage<Props> = ({ radioChannel }) => {
  const isSpMode = useIsSpMode();
  const { state: addRadioResponseState, addRadioResponse } =
    useAddRadioResponse();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors: formErrors },
    reset,
    setValue,
  } = useForm<AddRadioResponseInput>();
  const { currentUser } = useAppContext();

  const isSendButtonDisabled =
    addRadioResponseState.__type !== "ready" ||
    formErrors.content !== undefined ||
    formErrors.radioSegmentId !== undefined ||
    watch("content") === "" ||
    watch("radioSegmentId") === Number(EMPTY_OPTION_VALUE);

  const onSubmit: SubmitHandler<AddRadioResponseInput> = useCallback(
    async (data) => {
      if (addRadioResponseState.__type !== "ready") {
        return;
      }

      if (
        formErrors.content !== undefined ||
        formErrors.radioSegmentId !== undefined
      ) {
        return;
      }

      await addRadioResponse(addRadioResponseInputSchema.parse(data))
        .then((success) => success && reset())
        .catch((error) => console.error(error));
    },
    [addRadioResponseState, formErrors, reset, addRadioResponse]
  );

  return (
    <>
      <PageHead
        title={`${radioChannel.title} | お便りフォーム`}
        ogDescription={`【${radioChannel.title}】へのお便りはここから！`}
        ogUrl={`https://www.fussy.fun/radio/${radioChannel.id}`}
      />
      <Layout layoutType={"YTLayout"}>
        <main css={rootStyle}>
          <h1>「{radioChannel.title}」のお便りフォーム</h1>
          <section css={sectionStyle}>
            <p>発行者：{radioChannel.customer.name}</p>
            <p>ポイント：Creator 5pt</p>
          </section>
          {currentUser === null && (
            <div css={createAccountLeadContainerStyle}>
              <CreateAccountLead
                label={"お便りを送るにはログインする必要があります"}
              />
            </div>
          )}

          {addRadioResponseState.__type === "ready" && (
            <form css={formStyle} onSubmit={handleSubmit(onSubmit)}>
              <select {...register("radioSegmentId")} css={selectStyle}>
                {options(radioChannel.radioSegments, radioChannel.id).map(
                  (optionItem) => (
                    <option value={optionItem.value} key={optionItem.value}>
                      {optionItem.label}
                    </option>
                  )
                )}
              </select>
              <textarea
                {...register("content", { required: true, minLength: 1 })}
                rows={isSpMode ? 10 : 20}
                placeholder={textAreaPlaceholderText}
                css={textAreaStyle}
              />
              <div css={uploadFileControllerContainerStyle}>
                <UploadFileController setValue={setValue} />
              </div>
              <div css={buttonContainerStyle}>
                <Button
                  onClick={handleSubmit(onSubmit)}
                  disabled={isSendButtonDisabled}
                  variant="contained"
                  color="primary"
                  sx={{
                    width: "fit-content",
                  }}
                >
                  お便りを送信する
                </Button>
              </div>
            </form>
          )}

          {addRadioResponseState.__type === "success" && (
            <section css={sectionStyle}>
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  gap: 1rem;

                  #send-message {
                    font-size: 1.5rem;
                    ${mediaQueryIsMobile(css`
                      font-size: 1.25rem;
                    `)};
                    font-weight: bold;
                    margin-top: 1.5rem;
                  }

                  #link-quest {
                    padding: 0.5rem 1rem;
                    margin-top: 1rem;
                    margin-bottom: 2rem;
                    background: #333;
                    color: #fff;
                    border-radius: 0.25rem;
                    text-decoration: none;
                    transition: background 0.2s;
                  }
                `}
              >
                <p id="send-message">お便りを送信しました！</p>
                <p id="label-do-quest">
                  {radioChannel.category.name}
                  の他のクエストも実行して応援しよう！
                </p>
                <Link
                  id="link-quest"
                  href={`/category/${radioChannel.category.id}`}
                >
                  クエストを見る
                </Link>
              </div>
            </section>
          )}

          {addRadioResponseState.__type === "error" && (
            <section css={sectionStyle}>
              <p>お便りの送信に失敗しました。</p>
              {addRadioResponseState.error.response.errors?.map((error) => (
                <p key={error.message}>{error.message}</p>
              ))}
            </section>
          )}
        </main>
      </Layout>
    </>
  );
};

const rootStyle = css`
  ${mediaQueryIsDesktop(css`
    max-width: 800px;
    margin: 0 auto;
  `)};

  ${mediaQueryIsMobile(css`
    padding: 1rem;
    margin: 0 auto;
    width: 100%;

    h1 {
      font-size: 1.5rem;
    }
  `)};
`;

const sectionStyle = css`
  background: rgb(238, 238, 238);
  color: #000;
  padding: 0.5rem;
  border-radius: 0.25rem;
  margin-top: 1rem;

  p {
    margin: 0;
  }
`;

const createAccountLeadContainerStyle = css`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-top: 2rem;
`;

const formStyle = css`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
`;

const selectStyle = css`
  background: #fff;
  border: 1px solid #ccc;
  padding: 0.5rem;
  border-radius: 0.25rem;
  font-size: 1rem;
  color: #333;
  cursor: pointer;
  transition: border-color 0.2s;
  outline: none;
  width: 100%;
  appearance: none;
  text-align: left;
`;

const textAreaStyle = css`
  background: #fff;
  border: 1px solid #ccc;
  padding: 0.5rem;
  border-radius: 0.25rem;
  font-size: 1rem;
  color: #333;
  cursor: pointer;
  transition: border-color 0.2s;
  outline: none;
  width: 100%;
  resize: vertical;
`;

const uploadFileControllerContainerStyle = css`
  margin-top: 1rem;
`;

const buttonContainerStyle = css`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1rem;
`;

function useFileUpload(setValue: UseFormSetValue<AddRadioResponseInput>) {
  const [files, setFiles] = useState<Omit<UploadedFileProps, "removeFile">[]>(
    []
  );
  const [isUploading, setIsUploading] = useState(false);
  const client = useGraphqlClientWithAuth();

  const addFile = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      setIsUploading(true);
      const file = e.target.files?.[0];

      if (!client || !file) {
        setIsUploading(false);
        return;
      }

      const imageData = await readBlobAsDataUrl(file);
      const res = await client
        .uploadFileForRadioResponse({
          input: {
            base64EncodedFile: imageData,
            fileName: file.name,
            public: false,
          },
        })
        .catch((e) => {
          if (e instanceof ClientError) {
            const extensionsList = (e.response.errors ?? []).map(
              (error) => error.extensions
            );
            const isUserInvalid =
              extensionsList
                .map((extensions) =>
                  errorExtensionsSchema.safeParse(extensions)
                )
                .filter(
                  (exts) =>
                    exts.success &&
                    exts.data.code === "FUSSY_SCHEMA/ARGUMENT_ERROR"
                ).length > 0;
            if (isUserInvalid) {
              alert("ファイルのアップロードに失敗しました。");
            }
          }
          console.error(e.response.errors);
          setIsUploading(false);
          return null;
        });

      if (!res) {
        setIsUploading(false);
        return;
      }

      const newFiles = [
        ...files,
        {
          reference: {
            id: res.uploadFile.mediumReference.id,
            url: res.uploadFile.url,
            mediumType: res.uploadFile.mediumReference.mediumType,
          },
          position: files.length + 1,
        },
      ];

      setFiles(newFiles);
      setValue(
        "userMediumReferenceIds",
        newFiles.map((file) => file.reference.id)
      );
      setIsUploading(false);
    },
    [setIsUploading, client, files, setValue]
  );

  const removeFile = useCallback(
    (id: number) => {
      const newFiles = files.filter((file) => file.reference.id !== id);
      setFiles(newFiles);
      setValue(
        "userMediumReferenceIds",
        newFiles.map((file) => file.reference.id)
      );
    },
    [setFiles, files, setValue]
  );

  return {
    files,
    isUploading,
    addFile,
    removeFile,
  };
}

const UploadFileController = ({
  setValue,
}: {
  setValue: UseFormSetValue<AddRadioResponseInput>;
}) => {
  const { files, isUploading, addFile, removeFile } = useFileUpload(setValue);

  return (
    <div>
      <UploadFile addFile={addFile} isUploading={isUploading} />
      <div css={uploadedFileListContainerStyle}>
        {files.map((file) => (
          <UploadedFile
            {...file}
            removeFile={removeFile}
            key={file.reference.id}
          />
        ))}
      </div>
    </div>
  );
};

const uploadedFileListContainerStyle = css`
  margin-top: 1rem;
  ${mediaQueryIsDesktop(css`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 1rem;
    margin-top: 1rem;
    width: 100%;
    justify-content: center;
    align-items: center;
    justify-items: center;
    padding: 1rem;
    border-radius: 0.25rem;
  `)};

  ${mediaQueryIsMobile(css`
    display: flex;
    flex-flow: column;
    gap: 1rem;
  `)};
`;

type UploadedFileProps = {
  reference: {
    id: number;
    url: string;
    mediumType: UploadFileForRadioResponseMutation["uploadFile"]["mediumReference"]["mediumType"];
  };
  position: number;
  removeFile: (id: number) => void;
};

const UploadFile = ({
  addFile,
  isUploading,
}: {
  addFile: (e: ChangeEvent<HTMLInputElement>) => void;
  isUploading: boolean;
}) => {
  return (
    <div>
      <input
        type="file"
        accept="image/*,video/*,audio/*"
        onChange={addFile}
        multiple={false}
        disabled={isUploading}
      />
      {isUploading && <p>ファイルをアップロード中...</p>}
    </div>
  );
};

const UploadedFile = ({ reference, removeFile }: UploadedFileProps) => {
  const theme = useTheme();
  return (
    <div css={mediaDisplayContainerStyle(theme)}>
      {match(reference.mediumType)
        .with(MediumType.Video, () => (
          <video src={reference.url} controls width={"100%"} />
        ))
        .with(MediumType.Audio, () => <audio src={reference.url} controls />)
        .with(MediumType.Image, () => (
          <img
            src={reference.url}
            alt={"アップロードした画像"}
            width={"100%"}
          />
        ))
        .exhaustive()}
      <div onClick={() => removeFile(reference.id)}>削除</div>
    </div>
  );
};

const mediaDisplayContainerStyle = (theme: Theme) => css`
  width: 100%;
  padding: 1rem;
  background: ${theme.palette.background.paper};

  ${mediaQueryIsDesktop(css`
    width: 300px;
    height: auto;
  `)};
`;

export default RadioChannelDetailPage;
